import { useI18n } from 'vue-i18n';
import { TWILIO_CONTENT_TEMPLATE_TYPES } from 'shared/constants/messages';

/**
 * Composable for content template utilities and helpers.
 * @returns {Object} An object containing content template helper methods.
 */
export function useContentTemplates() {
  const { t } = useI18n();

  /**
   * Get the display name for a template category.
   * @param {string} category - The category code.
   * @returns {string} The localized category name.
   */
  const getCategoryDisplayName = category => {
    if (!category) return t('CONTENT_TEMPLATES.CATEGORIES.UNKNOWN');

    const categoryKey = category.toUpperCase();
    const translationKey = `CONTENT_TEMPLATES.CATEGORIES.${categoryKey}`;

    return t(
      translationKey,
      category.charAt(0).toUpperCase() + category.slice(1)
    );
  };

  /**
   * Get the display name for a template type.
   * @param {Object} template - The template object.
   * @returns {string} The localized template type name.
   */
  const getTemplateType = template => {
    if (template.template_type === TWILIO_CONTENT_TEMPLATE_TYPES.MEDIA) {
      return t('CONTENT_TEMPLATES.PICKER.TYPES.MEDIA');
    }
    if (template.template_type === TWILIO_CONTENT_TEMPLATE_TYPES.QUICK_REPLY) {
      return t('CONTENT_TEMPLATES.PICKER.TYPES.QUICK_REPLY');
    }
    if (
      template.template_type === TWILIO_CONTENT_TEMPLATE_TYPES.CALL_TO_ACTION
    ) {
      return t('CONTENT_TEMPLATES.PICKER.TYPES.CALL_TO_ACTION');
    }
    return t('CONTENT_TEMPLATES.PICKER.TYPES.TEXT');
  };

  /**
   * Get the display name for a template status.
   * @param {string} status - The status code.
   * @returns {string} The localized status name.
   */
  const getStatusDisplayName = status => {
    if (!status) return t('CONTENT_TEMPLATES.STATUS.APPROVED');

    const statusKey = status.toUpperCase().replace(/-/g, '_');
    const translationKey = `CONTENT_TEMPLATES.STATUS.${statusKey}`;

    return t(translationKey, status.charAt(0).toUpperCase() + status.slice(1));
  };

  /**
   * Get the CSS class for a template status.
   * @param {string} status - The status code.
   * @returns {string} The CSS class for styling the status.
   */
  const getStatusClass = status => {
    if (!status) return 'bg-n-teal-9/10 text-n-teal-11';

    const statusClassMap = {
      approved: 'bg-n-teal-9/10 text-n-teal-11',
      pending: 'bg-n-amber-9/10 text-n-amber-11',
      rejected: 'bg-n-ruby-9/10 text-n-ruby-11',
      unsubmitted: 'bg-n-blue-9/10 text-n-blue-11',
      in_review: 'bg-n-alpha-9/10 text-n-alpha-11',
      unknown: 'bg-n-slate-9/10 text-n-slate-11',
    };

    return statusClassMap[status] || 'bg-n-slate-9/10 text-n-slate-11';
  };

  return {
    getCategoryDisplayName,
    getTemplateType,
    getStatusDisplayName,
    getStatusClass,
  };
}
